import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Smartphone, Share, PlusSquare, MoreVertical, Download, CheckCircle2, Pin, BellRing, X } from "lucide-react";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function InstallAppCard() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const ios = isIOS();

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  if (installed) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="install-app-done">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-800 font-medium">You're using Adventure Rock as an installed app — nice! It opens straight from your home screen.</p>
      </div>
    );
  }

  return (
    <div className="bg-navy text-white rounded-2xl p-5" data-testid="install-app-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-turquoise/20 flex items-center justify-center shrink-0"><Smartphone className="w-6 h-6 text-turquoise" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-lg">Add Adventure Rock to your phone</p>
          <p className="text-white/60 text-sm mt-0.5">Install it like an app for one-tap access to bookings, lunches, pickups and receipts — no app store needed.</p>

          {deferred && (
            <Button onClick={install} className="mt-3 bg-turquoise text-navy font-bold rounded-full" data-testid="install-app-btn">
              <Download className="w-4 h-4 mr-1" /> Install app
            </Button>
          )}

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-xl p-3" data-testid="install-steps-ios">
              <p className="font-semibold text-sm flex items-center gap-1.5"><Share className="w-4 h-4 text-turquoise" /> iPhone / iPad (Safari)</p>
              <ol className="text-sm text-white/70 mt-2 space-y-1 list-decimal list-inside">
                <li>Open this site in <b>Safari</b>.</li>
                <li>Tap the <b>Share</b> button <Share className="inline w-3.5 h-3.5" /> at the bottom.</li>
                <li>Choose <b>Add to Home Screen</b> <PlusSquare className="inline w-3.5 h-3.5" />.</li>
                <li>Tap <b>Add</b> — the icon appears on your home screen.</li>
              </ol>
            </div>
            <div className="bg-white/5 rounded-xl p-3" data-testid="install-steps-android">
              <p className="font-semibold text-sm flex items-center gap-1.5"><MoreVertical className="w-4 h-4 text-turquoise" /> Android (Chrome)</p>
              <ol className="text-sm text-white/70 mt-2 space-y-1 list-decimal list-inside">
                <li>Open this site in <b>Chrome</b>.</li>
                <li>Tap the <b>⋮ menu</b> (top right).</li>
                <li>Choose <b>Install app</b> or <b>Add to Home screen</b>.</li>
                <li>Tap <b>Install</b> — you'll find it with your other apps.</li>
              </ol>
            </div>
          </div>
          {ios && <p className="text-[11px] text-turquoise mt-3">On iPhone this only works in Safari — if you're in another browser, open the site in Safari first.</p>}
        </div>
      </div>
    </div>
  );
}

export function PinnedWelcome() {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const askNotif = async () => {
    if (typeof Notification === "undefined") return toast.error("Your browser doesn't support notifications.");
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p === "granted") toast.success("Notifications are on — we'll keep you posted!");
      else toast("You can turn notifications on anytime from your browser settings.");
    } catch { /* ignore */ }
  };
  return (
    <div className="bg-gradient-to-br from-navy to-ocean text-white rounded-2xl p-5" data-testid="pinned-welcome">
      <div className="flex items-center gap-2">
        <Pin className="w-4 h-4 text-gold" />
        <p className="font-display font-bold text-lg">Welcome to Mission Control! 🧭</p>
      </div>
      <p className="text-white/70 text-sm mt-1">Two quick things for the best experience:</p>
      <ul className="text-white/80 text-sm mt-2 space-y-1.5 list-disc list-inside">
        <li><b>Add Adventure Rock to your phone's home screen</b> — the steps for iPhone &amp; Android are just below.</li>
        <li><b>Turn on notifications</b> so you never miss a pickup alert, payment reminder or lunch cut-off.</li>
      </ul>
      {perm !== "granted" && perm !== "unsupported" && (
        <Button onClick={askNotif} className="mt-3 bg-gold text-navy font-bold rounded-full" data-testid="pinned-welcome-notif-btn">
          <BellRing className="w-4 h-4 mr-1" /> Turn on notifications
        </Button>
      )}
      {perm === "granted" && <p className="text-emerald-300 text-sm mt-3 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Notifications are on.</p>}
    </div>
  );
}

export function InstallNudge() {
  const [hide, setHide] = useState(isStandalone() || (typeof localStorage !== "undefined" && localStorage.getItem("ar_install_nudge_dismissed") === "1"));
  if (hide) return null;
  const dismiss = () => { try { localStorage.setItem("ar_install_nudge_dismissed", "1"); } catch { /* ignore */ } setHide(true); };
  return (
    <div className="mt-4 flex items-center gap-2 bg-turquoise/10 border border-turquoise/30 rounded-xl px-3 py-2" data-testid="install-nudge">
      <Smartphone className="w-4 h-4 text-turquoise shrink-0" />
      <p className="text-sm text-navy flex-1">Add Adventure Rock to your home screen for one-tap access. <Link to="/dashboard/messages" className="font-semibold text-turquoise underline" data-testid="install-nudge-link">See how</Link></p>
      <button onClick={dismiss} className="text-slate-400 hover:text-slate-600" data-testid="install-nudge-dismiss" aria-label="Dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
